import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMessagePartText } from "@assistant-ui/react";
import { Streamdown } from "streamdown";
import remarkMath from "remark-math";
import remarkCjkFriendly from "remark-cjk-friendly";
import rehypeKatex from "rehype-katex";
import { useAuth } from "@/auth/OidcProvider";
import {
  downloadWithAuth,
  isAuthFetchable,
  isBrowserPreviewable,
  previewWithAuth,
} from "./attachments/authDownload";

// Streamdown ships remark-math + rehype-katex internally but its
// defaultRehypePlugins is [] (no rendering) and its bundled remark-math
// runs with singleDollarTextMath:false (inline $x$ disabled). We want
// both inline $H_2O$ and display $$6CO_2 + 6H_2O \to ...$$ to render
// across chemistry/math agent responses, so we override the plugin
// arrays explicitly.
// remark-cjk-friendly amends CommonMark's emphasis flanking rules for CJK:
// without it, `**` adjacent to fullwidth punctuation (是**"密集"**——) fails
// to open/close, rendering literal ** and cascading mispaired bold through
// the rest of the paragraph.
const remarkPlugins: NonNullable<
  React.ComponentProps<typeof Streamdown>["remarkPlugins"]
> = [[remarkMath, { singleDollarTextMath: true }], remarkCjkFriendly];
const rehypePlugins: NonNullable<
  React.ComponentProps<typeof Streamdown>["rehypePlugins"]
> = [rehypeKatex];

// Artifact download links arrive as "下载 DOCX — <标题>" / "📥 Download DOCX
// — <title>" (the Go tool emits "[📥 Download DOCX](url)" and the persona
// re-labels it with the document title). The redundant "下载 <格式> —"
// prefix wastes width — the link styling + surrounding copy already say
// it's a download — so strip it and keep just the title (2026-07-27 ask).
// Only applied to auth-fetchable artifact links; plain external links keep
// their text untouched. Labels without a title after the prefix (bare
// "下载 DOCX") are left as-is.
// 「点击打开:」是 2026-09-02 新增的一种前缀 —— 模型自己加的,和「下载 —」
// 同样冗余(链接样式已经说明它可点),且分隔符是全角冒号而不是破折号。

// collectArtifactHrefs walks a hast node and returns the auth-fetchable
// /uploads/ hrefs it contains, deduplicated in order.
function collectArtifactHrefs(node: unknown): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (n: unknown): void => {
    if (!n || typeof n !== "object") return;
    const el = n as { tagName?: string; properties?: { href?: unknown }; children?: unknown[] };
    const href = el.properties?.href;
    if (el.tagName === "a" && typeof href === "string" && isAuthFetchable(href) && !seen.has(href)) {
      seen.add(href);
      out.push(href);
    }
    el.children?.forEach(walk);
  };
  walk(node);
  return out;
}

const downloadLabelPrefixRE =
  /^(?:📥\s*)?(?:点击打开|点击查看|下载|Download|Open)\s*(?:DOCX|PPTX|PDF|Markdown|MD|文档|课件|文件)?\s*[—–\-::]\s*(.+)$/iu;

function stripDownloadLabelPrefix(children: React.ReactNode): React.ReactNode {
  const flat = Array.isArray(children) ? children : [children];
  if (flat.length !== 1 || typeof flat[0] !== "string") return children;
  const m = downloadLabelPrefixRE.exec(flat[0].trim());
  return m ? m[1] : children;
}

export function MarkdownText({
  text,
  isStreaming = false,
  authHeader,
}: {
  text: string;
  isStreaming?: boolean;
  // When provided, links pointing at Nova OS auth-required endpoints
  // (/uploads/<name> and /v1/conversations/<cid>/attachments/<aid>/download)
  // are intercepted: clicks do a JWT-authed fetch + Blob download instead
  // of letting the browser navigate (which would 401). Omit in tests or
  // other non-auth contexts; links then behave as plain external links.
  authHeader?: Record<string, string>;
}): JSX.Element {
  const { t } = useTranslation();
  // Accepted for API stability but intentionally unused: mode is now
  // pinned to "static" (see the Streamdown mode note below), so the
  // streaming flag no longer selects the render mode.
  void isStreaming;
  // 产物链接点开失败时的可见提示。旧行为只 console.error,界面毫无反应 ——
  // 而失败的主因恰恰是"产物压根没生成",用户最需要知道的就是这件事。
  const [linkError, setLinkError] = useState<string | null>(null);
  // Memoize the components map so its reference stays stable across
  // streaming re-renders. A fresh inline object on every token defeats
  // Streamdown's per-block memoization → it re-renders every block on
  // every chunk → the whole message repaints each token, which shows up
  // as flicker on real GPUs (invisible in headless software rendering).
  // authHeader is already useMemo-stable (OidcProvider), so this map only
  // rebuilds when the auth token actually changes.
  const components = useMemo<
    NonNullable<React.ComponentProps<typeof Streamdown>["components"]>
  >(
    () => ({
      a: ({ children, node: _node, href, ...props }) => (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            if (authHeader && href && isAuthFetchable(href)) {
              e.preventDefault();
              // 能渲染的(HTML 报告/PDF/图片)走预览 —— 预览页里浏览器
              // 自带下载和打印,所以不损失能力;Office 格式仍走下载。
              // 失败必须让用户看见 —— 产物可能压根没生成(见 authDownload.ts)。
              setLinkError(null);
              const open = isBrowserPreviewable(href)
                ? previewWithAuth(href, authHeader)
                : downloadWithAuth(href, authHeader);
              void open.catch((err: Error) => {
                setLinkError(err.message);
              });
            }
          }}
        >
          {href && isAuthFetchable(href)
            ? stripDownloadLabelPrefix(children)
            : children}
        </a>
      ),
      // 链接点开是预览(见上)。Jarod 2026-09-03:「目前可以直接打开,但是没有
      // 下载提醒了,能不能给个下载 ui」—— 每个含产物链接的段落末尾跟一个
      // 明确的下载按钮,走同一套带鉴权的下载。放在 <p> 级,不塞进 <a> 里。
      p: ({ children, node, ...props }) => {
        const links = authHeader ? collectArtifactHrefs(node) : [];
        if (links.length === 0) {
          return <p {...props}>{children}</p>;
        }
        return (
          <p {...props}>
            {children}
            {links.map((h) => (
              <button
                key={h}
                type="button"
                className="teacher-artifact-download ml-2 inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 align-middle"
                title={String(t("documents.files.downloadHint"))}
                onClick={() => {
                  if (!authHeader) return;
                  setLinkError(null);
                  void downloadWithAuth(h, authHeader).catch((err: Error) => setLinkError(err.message));
                }}
              >
                ⬇ {t("documents.files.download")}
              </button>
            ))}
          </p>
        );
      },
      strong: ({ children, node: _node, ...props }) => (
        <strong {...props}>{children}</strong>
      ),
    }),
    [authHeader, t],
  );
  return (
    <>
      {linkError && (
        <div
          role="alert"
          className="mb-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {linkError}
        </div>
      )}
    <Streamdown
      className="teacher-markdown"
      // Pinned to "static" + parseIncompleteMarkdown off. Streamdown's
      // "streaming" path re-parses the text and rebuilds the whole block
      // tree on every token; on slow/real devices that flashes empty frames
      // — the answer blanks out then reappears (the reported flicker). It's
      // invisible in headless (renders too fast) but reproduces under CPU
      // throttling: content length oscillates …161→0→177…. Static mode diffs
      // the text in place instead of tearing down + rebuilding blocks.
      // isStreaming is kept on the prop for API stability but no longer
      // drives mode.
      mode="static"
      parseIncompleteMarkdown={false}
      controls={{ table: false, code: true, mermaid: true }}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {text}
    </Streamdown>
    </>
  );
}

export function MarkdownMessageText(): JSX.Element {
  const { text, status } = useMessagePartText();
  const { authHeader } = useAuth();
  return (
    <MarkdownText
      text={text}
      isStreaming={status.type === "running"}
      authHeader={authHeader}
    />
  );
}
